import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { setUser } from '@/store/userSlice';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Busy, Field } from '@/components/kit';
import { FormDialog } from '@/components/inputs';
import { markMyEmailVerified } from '@/shared/account';
import { cn } from '@/lib/utils';
import { useT } from '@/shared/i18n';
import { C } from '@/shared/locales/checkout/keys';

const CODE_RE = /^\d{6}$/;

const sameEmail = (a, b) => !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Optional "Verified Email" checkbox. Checked when the account email is confirmed.
 * Otherwise it offers a 6-digit code by email. Never required to continue.
 * The code dialog renders in a portal, but its submit still bubbles through React to any parent form,
 * so the wrapper stops it.
 */
export default function VerifiedEmailCheck({ user, email, verified, onVerified, className }) {
  const { t } = useT();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);

  const account = user?.emailAddress ?? '';
  const matches = sameEmail(email, account);

  async function send() {
    if (!account || sending) return;
    setSending(true);
    try {
      const res = await sdk.session.resendVerificationCode({ emailAddress: account });
      if (res?.ok === false) {
        toast.error(res.error?.message || t(C.vSendFail));
        return;
      }
      toast.success(t(C.vSent, { email: account }));
      setError('');
      setCode('');
      setOpen(true);
    } catch (err) {
      toast.error(err?.message || t(C.vSendFail));
    } finally {
      setSending(false);
    }
  }

  async function verify() {
    const value = code.trim();
    if (!CODE_RE.test(value)) return setError(t(C.vEnter));
    setSaving(true);
    setError('');
    try {
      const res = await sdk.session.verifyEmail({ emailAddress: account, code: value, type: 'signup' });
      if (res?.ok === false) {
        setError(res.error?.message || t(C.vBad));
        return;
      }
      try {
        const fresh = await sdk.session.getUser();
        if (fresh) dispatch(setUser(fresh));
      } catch {
        // The session refreshes on the next load; the order still carries the flag.
      }
      await markMyEmailVerified(user);
      toast.success(t(C.vDone));
      setOpen(false);
      onVerified?.();
    } catch (err) {
      setError(err?.message || t(C.vBad));
    } finally {
      setSaving(false);
    }
  }

  let hint = t(C.vHintOpt);
  if (verified) hint = t(C.vHintOk);
  else if (!account) hint = t(C.vHintSignIn);
  else if (!matches) hint = t(C.vHintOnly, { email: account });

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2.5',
        verified ? 'border-success/40 bg-success/10' : 'border-border',
        className,
      )}
      onSubmit={(e) => e.stopPropagation()}
    >
      <label htmlFor="verified-email" className="flex min-w-0 items-start gap-2.5">
        <Checkbox
          id="verified-email"
          className="mt-0.5"
          checked={!!verified}
          disabled={verified || !matches || sending}
          onCheckedChange={(v) => v === true && send()}
        />
        <span className="min-w-0">
          <span className="text-sm font-medium">{t(C.vLabel)}</span>
          <span className="block text-xs text-muted-foreground">{hint}</span>
        </span>
      </label>
      {!verified && matches && (
        <Button type="button" variant="outline" size="sm" onClick={send} disabled={sending}>
          <Busy busy={sending} label={t(C.vSend)} busyLabel={t('Sending…')} />
        </Button>
      )}

      <FormDialog
        open={open}
        onOpenChange={setOpen}
        title={t(C.vTitle)}
        description={t(C.vDesc, { email: account })}
        onSubmit={verify}
        saving={saving}
        submitLabel={t(C.vVerify)}
        busyLabel={t(C.vVerifying)}
      >
        <Field label={t(C.vCode)} htmlFor="verify-code">
          <Input
            id="verify-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="123456"
            className="text-center text-lg tracking-widest"
            autoFocus
          />
        </Field>
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          {t(C.vNoEmail)}{' '}
          <button
            type="button"
            onClick={send}
            disabled={sending}
            className="text-primary underline underline-offset-2 disabled:opacity-60"
          >
            {sending ? t('Sending…') : t(C.vNew)}
          </button>
        </p>
      </FormDialog>
    </div>
  );
}
